import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, ActivityIndicator, Keyboard, KeyboardAvoidingView, Platform,
} from 'react-native';
import Modal from 'react-native-modal';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import Button from '../../../components/Button';
import isMobile from '../../../components/IsMobile';
import ResolvedIconMobile from '../../../components/16pxIcons/ResolvedIconMobile';
import ResolvedIcon from '../../../components/ResolvedIcon';
import updateTicketStatus from '../../../requests/Tickets/UpdateTicketStatus';
import { TicketStatus } from '../../../constants/Types';
import { TICKET_STATUS_UPDATE_FAILURE } from '../../../constants/AlertMessages';
import PressableStyled from '../../../components/PressableStyled';
import SparklesIcon from '../../../components/20pxIcons/SparklesIcon';
import getTicketResolutionNote from '../../../requests/Tickets/GetTicketResolutionNote';
import BouncingDots from '../../../components/BouncingDots';

const defaultError: string = TICKET_STATUS_UPDATE_FAILURE;
const maxLength: number = 1000;

interface ClosureModalProps {
  isVisible: boolean,
  hide: () => void,
  id: string,
  resolve: () => void,
}

const ClosureModal = ({
  isVisible, hide, id, resolve,
}: ClosureModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [value, onChangeText] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [assistedNote, setAssistedNote] = useState<boolean>(false);
  const [loadingAINote, setLoadingAINote] = useState<boolean>(false);

  useEffect(() => setError(null), [value]);

  const save: () => void = () => {
    setLoading(true);
    updateTicketStatus(id, TicketStatus.COMPLETED, value?.trim()).then((response) => {
      if (response && !response.isError) {
        resolve();
        setSubmitted(true);
      } else setError(response?.errorText ?? defaultError);
    }).catch((e) => {
      setError(e?.message ?? defaultError);
    }).finally(() => setLoading(false));
  };

  const reset: () => void = () => {
    setLoading(false);
    onChangeText('');
    setSubmitted(false);
  };

  return <Modal
    {...{ isVisible }}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    style={Style.modal}
    onModalHide={reset}
  >
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={20}
      testID='closure-note-modal'
    >
      <View style={[Style.modalContent, styles.container]}>
        <View style={styles.iconContainer}>
          {isMobile ? <ResolvedIconMobile /> : <ResolvedIcon />}
        </View>
        <Text style={styles.title}>
          {submitted ? 'Ticket has been resolved!' : 'Tell us how you solved this ticket'}
        </Text>
        {submitted ? null : <View style={[Style.row, { marginBottom: 4 }]}>
          <Text style={{
            fontFamily: 'Roboto_400Regular', fontSize: 16, lineHeight: 24, color: Colors.light.hereworksBlack400,
          }}>Closure Note</Text>
          <PressableStyled
            style={Style.row}
            disabled={loadingAINote}
            onPress={() => {
              setLoadingAINote(true);
              getTicketResolutionNote(id)
                .then((res) => {
                  if (res && !res.isError && res.resolutionNote) {
                    onChangeText(res.resolutionNote);
                    setAssistedNote(true);
                  } else if (res && res.isError) {
                    setError(res.errorText ?? defaultError);
                  } else if (!res?.resolutionNote) {
                    setError(defaultError);
                  }
                })
                .catch((e) => {
                  setError(e?.message ?? defaultError);
                })
                .finally(() => {
                  setLoadingAINote(false);
                });
            }}
          >
            <SparklesIcon />
            <Text style={{
              fontFamily: 'Roboto_400Regular', fontSize: 14, color: Colors.light.hereworksBlue500, marginLeft: 4,
            }}>Add AI Summary</Text>
          </PressableStyled>
        </View>}
        {submitted ? <Button
          affirmative
          title='Close'
          onPress={hide}
          style={styles.closeButton}
          testID='close-resolved-modal-btn'
        /> : <>
          <View style={[assistedNote && styles.assistedNoteContainer]}>
            <TextInput
              multiline
              onBlur={Keyboard.dismiss}
              scrollEnabled
              style={[styles.text, styles.input, assistedNote && styles.assistedNote]}
              {...{ value, onChangeText, maxLength }}
              placeholder='Please leave a note'
              placeholderTextColor={styles.text.color}
            />
            {loadingAINote && <View style={{
              position: 'absolute', top: 2, left: 2, right: 2, bottom: 2, backgroundColor: '#fff',
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', padding: 8 }}>
                <BouncingDots />
                <Text style={{
                  fontFamily: 'Roboto_400Regular', fontSize: 14, color: Colors.light.hereworksBlack500, marginLeft: 4,
                }}>Generating AI summary...</Text>
              </View>
            </View>}
          </View>
          {error && <Text style={[styles.text, styles.errorText]}>{error}</Text>}
          <View style={[Style.row, styles.buttonContainer]}>
            <Button
              title='Cancel'
              onPress={hide}
            />
            <Button
              affirmative
              title='Confirm'
              onPress={save}
              disabled={loading || !(value?.length > 0)}
              testID='confirm-resolve-ticket-btn'
            />
          </View>
        </>}
        <ActivityIndicator
          size='large'
          style={Style.activityIndicator}
          animating={loading}
        />
      </View>
    </KeyboardAvoidingView>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksGreen400,
    padding: isMobile ? 24 : 40,
    width: isMobile ? '80%' : 414,
    alignSelf: 'center',
  },
  iconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    borderRadius: 32,
    backgroundColor: Colors.light.hereworksGreen400,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  title: {
    marginTop: isMobile ? 20 : 32,
    marginBottom: isMobile ? 36 : 52,
    fontSize: isMobile ? 20 : 28,
    lineHeight: isMobile ? 24 : 34,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksGreen400,
    textAlign: 'center',
    alignSelf: 'center',
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  input: {
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 5,
    color: Colors.light.hereworksBlack600,
    height: 108,
  },
  buttonContainer: {
    marginTop: isMobile ? 24 : 40,
    justifyContent: 'center',
  },
  closeButton: {
    flex: undefined,
  },
  errorText: {
    color: Colors.light.hereworksRed500,
  },
  characterLimitLabel: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack300,
  },
  assistedNote: {
    borderColor: Colors.light.hereworksBlue400,
    borderWidth: 2,
  },
  assistedNoteContainer: {
    borderWidth: 3,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
  },
});

export default ClosureModal;
